import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Trash2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { api } from '@/services/api';

interface StockAdjustment {
  id: string;
  adjustmentNumber: string;
  productId: string;
  adjustmentType: 'increase' | 'decrease';
  quantity: number;
  reason: 'damage' | 'loss' | 'theft' | 'correction' | 'return' | 'other';
  notes?: string;
  adjustedAt: string | Date;
  createdAt: string | Date;
  updatedAt: string | Date;
}

interface Product {
  id: string;
  sku: string;
  name: string;
  stockQuantity: number;
  price: number;
  category: string;
  taxRate: number;
  isActive: boolean;
  createdAt: string | Date;
  updatedAt: string | Date;
}

const StockAdjustmentsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const [formData, setFormData] = useState({
    productId: '',
    adjustmentType: 'increase' as 'increase' | 'decrease',
    quantity: '1',
    reason: 'correction' as StockAdjustment['reason'],
    notes: '',
  });

  const { data: adjustmentsData, isLoading, isError } = useQuery<{ adjustments: StockAdjustment[] }>({
    queryKey: ['stock_adjustments'],
    queryFn: async () => {
      const res = await api.get('/api/stock-adjustments');
      return res.data;
    },
  });

  const { data: productsData } = useQuery<{ products: Product[] }>({
    queryKey: ['products'],
    queryFn: async () => {
      const res = await api.get('/api/products');
      return res.data;
    },
  });

  const products = productsData?.products || [];
  const adjustments = adjustmentsData?.adjustments || [];

  const productById = useMemo(() => {
    const map = new Map<string, Product>();
    for (const p of products) map.set(p.id, p);
    return map;
  }, [products]);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return adjustments;

    return adjustments.filter((a) => {
      const p = productById.get(a.productId);
      return (
        (a.adjustmentNumber || '').toLowerCase().includes(q) ||
        (a.reason || '').toLowerCase().includes(q) ||
        (a.notes || '').toLowerCase().includes(q) ||
        (p?.name || '').toLowerCase().includes(q) ||
        (p?.sku || '').toLowerCase().includes(q)
      );
    });
  }, [adjustments, productById, searchTerm]);

  const openCreateModal = () => {
    setFormData({
      productId: '',
      adjustmentType: 'increase',
      quantity: '1',
      reason: 'correction',
      notes: '',
    });
    setIsCreateModalOpen(true);
  };

  const closeCreateModal = () => setIsCreateModalOpen(false);

  const createMutation = useMutation({
    mutationFn: async () => {
      const qty = parseInt(formData.quantity, 10);
      const payload = {
        productId: formData.productId,
        adjustmentType: formData.adjustmentType,
        quantity: qty,
        reason: formData.reason,
        notes: formData.notes.trim() || undefined,
      };

      const res = await api.post('/api/stock-adjustments', payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stock_adjustments'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      setIsCreateModalOpen(false);
      toast.success('Stock adjustment created');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to create stock adjustment');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/stock-adjustments/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stock_adjustments'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('Stock adjustment deleted');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to delete stock adjustment');
    },
  });

  const canSave = Boolean(formData.productId && Number.isFinite(parseInt(formData.quantity, 10)) && parseInt(formData.quantity, 10) > 0);

  const formatDateTime = (dateValue: string | Date) => {
    return new Date(dateValue).toLocaleString();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Stock Adjustments</h1>
          <p className="mt-1 text-sm text-gray-600">Correct and reconcile inventory levels</p>
        </div>
        <Button onClick={openCreateModal}>
          <Plus className="h-4 w-4 mr-2" />
          New Adjustment
        </Button>
      </div>

      <div className="card">
        <div className="card-body">
          <Input
            placeholder="Search adjustment #, product, reason, notes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={<Search className="h-4 w-4 text-gray-400" />}
          />
        </div>
      </div>

      <div className="card">
        <div className="card-header flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-900">Adjustments</h2>
          <div className="text-sm text-gray-600">Total: {filtered.length}</div>
        </div>

        <div className="card-body">
          {isLoading ? (
            <div className="text-sm text-gray-600">Loading adjustments...</div>
          ) : isError ? (
            <div className="text-sm text-red-600">Failed to load adjustments</div>
          ) : filtered.length === 0 ? (
            <div className="text-sm text-gray-600">No stock adjustments found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Qty</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filtered.map((a) => {
                    const p = productById.get(a.productId);
                    const typeLabel = a.adjustmentType === 'increase' ? 'Increase' : 'Decrease';
                    const typeClass = a.adjustmentType === 'increase' ? 'text-green-700 bg-green-50' : 'text-red-700 bg-red-50';

                    return (
                      <tr key={a.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {a.adjustmentNumber}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {formatDateTime(a.adjustedAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="font-medium">{p?.name || a.productId}</div>
                          <div className="text-xs text-gray-500">SKU: {p?.sku || '-'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${typeClass}`}>{typeLabel}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{a.quantity}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{a.reason}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{a.notes || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteMutation.mutate(a.id)}
                            loading={deleteMutation.isPending}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">New Stock Adjustment</h3>
              <button onClick={closeCreateModal} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Product *</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  value={formData.productId}
                  onChange={(e) => setFormData({ ...formData, productId: e.target.value })}
                >
                  <option value="">Select a product</option>
                  {products
                    .filter((p) => p.isActive)
                    .map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} (SKU: {p.sku}) - Stock: {p.stockQuantity}
                      </option>
                    ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    value={formData.adjustmentType}
                    onChange={(e) => setFormData({ ...formData, adjustmentType: e.target.value as any })}
                  >
                    <option value="increase">Increase</option>
                    <option value="decrease">Decrease</option>
                  </select>
                </div>

                <Input
                  label="Quantity *"
                  type="number"
                  min="1"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason *</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value as any })}
                >
                  <option value="correction">Correction</option>
                  <option value="damage">Damage</option>
                  <option value="loss">Loss</option>
                  <option value="theft">Theft</option>
                  <option value="return">Return</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <Input
                label="Notes"
                placeholder="Optional notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />

              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
                <Button variant="secondary" onClick={closeCreateModal}>
                  Cancel
                </Button>
                <Button onClick={() => createMutation.mutate()} loading={createMutation.isPending} disabled={!canSave}>
                  Create Adjustment
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockAdjustmentsPage;
